import { c as _c } from "react/compiler-runtime";
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useSendMessageUserGuest } from "./useSendMessageUserGuest";
import { InputA } from "@/components/atoms/Form/FormInput/InputA";
import { AlertTriangle, ChevronLeft, Key, Mail, User as UserIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import dynamic from "next/dynamic";
import realEstateAnimation from "./startMessage.json";
import { Button } from "@/components/ui/button";
import { User } from "@/types/me";
import SocialLogin from "@/components/pages/auth/SocialLogin";
import { Separator } from "@/components/ui/separator";
import { useModal } from "@/components/providers/modal-provider/ModalProvider";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/constants/queryKeys";
import { useState } from "react";
import { useCreateGoogleLeadConversationMutation } from "@/components/modules/chat/api/mutations/useCreateGoogleLeadConversationMutation";
import { useMobile } from "@/components/providers/MobileProvider";
const Lottie = dynamic(() => import("lottie-react"), {
  ssr: false,
  loading: () => <></>
});
type Props = {
  onLoginSuccess?: ({
    user,
    conversationId
  }: {
    user: User;
    conversationId: string;
  }) => void;
  recipientId: string;
  realEstateId?: string;
};
export function SendMessageUserGuest(t0) {
  const $ = _c(70);
  const {
    onLoginSuccess,
    recipientId,
    realEstateId
  } = t0;
  let t1;
  if ($[0] !== onLoginSuccess || $[1] !== realEstateId || $[2] !== recipientId) {
    t1 = {
      recipientId,
      realEstateId,
      onLoginSuccess
    };
    $[0] = onLoginSuccess;
    $[1] = realEstateId;
    $[2] = recipientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    form,
    onSubmit,
    isPending,
    error,
    mode,
    setMode,
    messageField,
    messageValidationAttempted,
    setMessageValidationAttempted,
    nameField,
    setNameValidationAttempted,
    t
  } = useSendMessageUserGuest(t1);
  const queryClient = useQueryClient();
  const {
    closeModalById,
    modalStack
  } = useModal();
  const [googleLoginError, setGoogleLoginError] = useState(null);
  const {
    mutate: createGoogleLead,
    isPending: isPendingGoogleLead
  } = useCreateGoogleLeadConversationMutation();
  const isMobile = useMobile();
  let t2;
  if ($[4] !== closeModalById || $[5] !== createGoogleLead || $[6] !== form || $[7] !== messageField || $[8] !== modalStack || $[9] !== onLoginSuccess || $[10] !== queryClient || $[11] !== realEstateId || $[12] !== recipientId || $[13] !== t) {
    t2 = user => {
      setGoogleLoginError(null);
      const storedProfile = typeof window !== "undefined" ? localStorage.getItem("googleLeadProfile") : null;
      const parsed = storedProfile ? JSON.parse(storedProfile) as any : null;
      const googleId = parsed?.googleId as string | undefined;
      const email = parsed?.email as string | undefined ?? user.email;
      const name = parsed?.name as string | undefined ?? user.name;
      const picture = parsed?.picture as string | undefined ?? (user as any).image ?? "";
      if (!googleId) {
        setGoogleLoginError(t("userAlreadyExists"));
        return;
      }
      const message = messageField?.trim();
      if (!message || message.length <= 3) {
        setGoogleLoginError(t("invalidMessage"));
        return;
      }
      createGoogleLead({
        googleId,
        email,
        name,
        picture,
        recipientId,
        message,
        realEstateId
      }, {
        onSuccess: data => {
          queryClient.setQueryData([QueryKeys.SESSION], data.data.user);
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.REAL_ESTATE]
          });
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.LOCATIONS]
          });
          form.reset();
          closeModalById(modalStack[modalStack.length - 1]?.id || "");
          onLoginSuccess?.({
            user: data.data.user,
            conversationId: data.data.conversation.id
          });
        },
        onError: () => {
          setGoogleLoginError(t("googleSendError"));
        }
      });
    };
    $[4] = closeModalById;
    $[5] = createGoogleLead;
    $[6] = form;
    $[7] = messageField;
    $[8] = modalStack;
    $[9] = onLoginSuccess;
    $[10] = queryClient;
    $[11] = realEstateId;
    $[12] = recipientId;
    $[13] = t;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const handleGoogleLoginSuccess = t2;
  let t3;
  if ($[15] !== form || $[16] !== onSubmit) {
    t3 = form.handleSubmit(onSubmit);
    $[15] = form;
    $[16] = onSubmit;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== mode || $[19] !== setMode) {
    t4 = mode === "login" && <Button onClick={() => setMode("message")} type="button" className="absolute left-8 top-20 h-10 w-10 border bg-gray-50 rounded-full" variant="ghost"><ChevronLeft className="w-5 h-5" /></Button>;
    $[18] = mode;
    $[19] = setMode;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="w-[80px] h-[80px]"><Lottie animationData={realEstateAnimation} loop={true} autoplay={true} style={{
        width: "100%",
        height: "100%"
      }} data-sentry-element="Lottie" data-sentry-source-file="SendMessageUserGuest.tsx" /></div>;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== form.control || $[23] !== messageField || $[24] !== messageValidationAttempted || $[25] !== mode || $[26] !== nameField || $[27] !== t) {
    t6 = mode === "message" ? <FormField control={form.control} name="message" render={t7 => {
      const {
        field
      } = t7;
      return <FormItem className=""><FormControl><Textarea id="message" placeholder={t("messagePlaceholder")} className="h-44 sm:w-full text-primary" {...field} /></FormControl>{messageValidationAttempted && <FormMessage className="pl-4" />}<FormField control={form.control} name="name" render={t8 => {
          const {
            field: field_0
          } = t8;
          return <FormItem><FormControl><InputA id="name" className="pl-3.5" placeholder={t("namePlaceholder")} {...field_0} /></FormControl><FormMessage className="pl-4" /></FormItem>;
        }} /><FormDescription className="mt-4">{t("privacyNotice")}{" "}<u className="font-semibold">{t("readPrivacyPolicy")}</u></FormDescription></FormItem>;
    }} /> : <div className="bg-gray-100 p-4 rounded-lg"><p className="text-zinc-700 line-clamp-6 text-ellipsis capitalize">{nameField}: {messageField}</p></div>;
    $[22] = form.control;
    $[23] = messageField;
    $[24] = messageValidationAttempted;
    $[25] = mode;
    $[26] = nameField;
    $[27] = t;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== form.control || $[30] !== mode || $[31] !== t) {
    t7 = mode === "login" && <div className="flex flex-col mt-2 gap-4"><FormField control={form.control} name="email" render={t8 => {
        const {
          field: field_1
        } = t8;
        return <FormItem><FormControl><InputA id="email" placeholder={t("emailPlaceholder")} startAdornment={<Mail size={16} className="text-gray-700" />} {...field_1} /></FormControl><FormMessage /></FormItem>;
      }} /><FormField control={form.control} name="password" render={t9 => {
        const {
          field: field_2
        } = t9;
        return <FormItem><FormControl><InputA type="password" id="password" placeholder={t("passwordPlaceholder")} autoComplete="new-password" startAdornment={<Key size={16} className="text-gray-700" />} {...field_2} /></FormControl><FormMessage /></FormItem>;
      }} /><FormDescription className="px-3">{t("identificationNotice")}</FormDescription></div>;
    $[29] = form.control;
    $[30] = mode;
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t6 || $[34] !== t7) {
    t8 = <div className="flex flex-col w-full gap-4 mt-4">{t6}{t7}</div>;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== error || $[37] !== googleLoginError) {
    t9 = (error || googleLoginError) && <div className="p-4 border flex justify-center mt-2 w-full shadow-lg rounded-xl"><AlertTriangle className="mx-auto mb-4 w-7 h-7 text-red-700" /><p className="text-red-600 text-center">{googleLoginError || error}</p></div>;
    $[36] = error;
    $[37] = googleLoginError;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== form || $[40] !== isPending || $[41] !== mode || $[42] !== setMessageValidationAttempted || $[43] !== setMode || $[44] !== setNameValidationAttempted || $[45] !== t) {
    t10 = mode === "message" && <Button onClick={async () => {
      const isValid = await form.trigger("message", {
        shouldFocus: true
      });
      const isNameValid = await form.trigger("name", {
        shouldFocus: true
      });
      setMessageValidationAttempted(true);
      setNameValidationAttempted(true);
      if (isValid && isNameValid) {
        setMode("login");
      }
    }} isLoading={isPending} variant="simpleDark" type="button" className="w-full mt-6 h-12 rounded-full sm:rounded-sm "><p className="text-white text-lg">{t("next")}</p></Button>;
    $[39] = form;
    $[40] = isPending;
    $[41] = mode;
    $[42] = setMessageValidationAttempted;
    $[43] = setMode;
    $[44] = setNameValidationAttempted;
    $[45] = t;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== isPending || $[48] !== isPendingGoogleLead || $[49] !== mode || $[50] !== t) {
    t11 = mode === "login" && <Button isLoading={isPending || isPendingGoogleLead} variant="simpleDark" className="w-full  h-12 rounded-full sm:rounded-sm "><p className="text-white text-xl">{t("send")}</p></Button>;
    $[47] = isPending;
    $[48] = isPendingGoogleLead;
    $[49] = mode;
    $[50] = t;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== handleGoogleLoginSuccess || $[53] !== isMobile || $[54] !== mode || $[55] !== t) {
    t12 = mode === "login" && <><div className="flex items-center w-full mt-8"><Separator className="flex-1" /><p className="mx-2 whitespace-nowrap">{t("orSendWith")}</p><Separator className="flex-1" /></div><div className="mt-6"><SocialLogin rounded={!!isMobile} onLoginSuccess={handleGoogleLoginSuccess} /></div></>;
    $[52] = handleGoogleLoginSuccess;
    $[53] = isMobile;
    $[54] = mode;
    $[55] = t;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  let t13;
  if ($[57] !== t10 || $[58] !== t11 || $[59] !== t12) {
    t13 = <div className="pt-4 w-full">{t10}{t11}{t12}</div>;
    $[57] = t10;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== t13 || $[62] !== t3 || $[63] !== t4 || $[64] !== t8 || $[65] !== t9) {
    t14 = <form className="w-full flex flex-col p-4 items-center sm:min-w-[800px] " onSubmit={t3}>{t4}{t5}{t8}{t9}{t13}</form>;
    $[61] = t13;
    $[62] = t3;
    $[63] = t4;
    $[64] = t8;
    $[65] = t9;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  let t15;
  if ($[67] !== form || $[68] !== t14) {
    t15 = <Form {...form} data-sentry-element="Form" data-sentry-component="SendMessageUserGuest" data-sentry-source-file="SendMessageUserGuest.tsx">{t14}</Form>;
    $[67] = form;
    $[68] = t14;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  return t15;
}